import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import repositoryContainer from '../../services/RepositoryContainer';
import { connectToChat } from '../../services/chatHub';

const PAGE_SIZE = 50;
const MAX_MESSAGE_LENGTH = 2000;

const messageMatches = (left, right) => (
  (left?.id && right?.id && left.id === right.id)
  || (left?.clientMessageId && right?.clientMessageId && left.clientMessageId === right.clientMessageId)
);

const mergeMessages = (current, incoming) => {
  const merged = [...current];
  incoming.filter(Boolean).forEach((message) => {
    const existingIndex = merged.findIndex((entry) => messageMatches(entry, message));
    if (existingIndex >= 0) merged[existingIndex] = { ...merged[existingIndex], ...message };
    else merged.push(message);
  });

  return merged.sort((left, right) => {
    const leftTime = new Date(left?.createdAtUtc || 0).getTime();
    const rightTime = new Date(right?.createdAtUtc || 0).getTime();
    return leftTime - rightTime;
  });
};

const getErrorMessage = (error) => {
  if (error?.status === 404) return 'A conversation is not available for this record.';
  if (error?.status === 401 || error?.status === 403) return 'This conversation cannot be opened on this device.';
  return error?.data?.message || 'The conversation could not be loaded. Please try again.';
};

const formatMessageTime = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-GH', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const ConversationModal = ({
  isOpen,
  onClose,
  audience,
  subjectType,
  subjectId,
  referenceCode,
  chatAccessToken,
}) => {
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [draft, setDraft] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [sendError, setSendError] = useState('');
  const [failedAttempt, setFailedAttempt] = useState(null);
  const messageListRef = useRef(null);
  const textareaRef = useRef(null);

  const isCustomer = audience === 'customer';
  const hasOlderMessages = messages.length < Number(conversation?.totalCount || 0);
  const canConnect = isCustomer
    ? Boolean(referenceCode && chatAccessToken)
    : Boolean(subjectId);

  const title = subjectType === 'bookingRequest' ? 'Request conversation' : 'Booking conversation';

  const requestPage = async (requestedPage) => {
    const raw = isCustomer
      ? await repositoryContainer.chat.getCustomerConversation(
          subjectType,
          referenceCode,
          chatAccessToken,
          requestedPage,
          PAGE_SIZE
        )
      : await repositoryContainer.chat.getCompanyConversation(
          subjectType,
          subjectId,
          requestedPage,
          PAGE_SIZE
        );
    return raw?.response ?? raw;
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.stopImmediatePropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;

    let active = true;
    setMessages([]);
    setConversation(null);
    setDraft('');
    setPage(1);
    setError('');
    setSendError('');
    setFailedAttempt(null);

    if (!canConnect) {
      setError('This conversation cannot be opened on this device.');
      return undefined;
    }

    setIsLoading(true);
    requestPage(1)
      .then((data) => {
        if (!active) return;
        setConversation(data);
        setMessages(mergeMessages([], data?.messages || []));
        window.requestAnimationFrame(() => {
          if (messageListRef.current) {
            messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
          }
          textareaRef.current?.focus();
        });
      })
      .catch((loadError) => {
        if (active) setError(getErrorMessage(loadError));
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    let connection = null;
    let disposed = false;
    connectToChat({
      audience,
      subjectType,
      subjectId,
      referenceCode,
      chatAccessToken,
    }, (message) => {
      if (!disposed) setMessages((current) => mergeMessages(current, [message]));
    })
      .then((createdConnection) => {
        if (disposed) createdConnection.stop().catch(() => {});
        else connection = createdConnection;
      })
      .catch(() => {});

    return () => {
      active = false;
      disposed = true;
      connection?.stop().catch(() => {});
    };
  // The modal is intentionally reloaded only when its record or visibility changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, audience, subjectType, subjectId, referenceCode, chatAccessToken, canConnect]);

  const loadOlderMessages = async () => {
    if (!hasOlderMessages || isLoadingOlder) return;
    const list = messageListRef.current;
    const previousHeight = list?.scrollHeight || 0;
    setIsLoadingOlder(true);
    setError('');
    try {
      const nextPage = page + 1;
      const data = await requestPage(nextPage);
      setConversation((current) => ({ ...current, ...data }));
      setMessages((current) => mergeMessages(data?.messages || [], current));
      setPage(nextPage);
      window.requestAnimationFrame(() => {
        if (list) list.scrollTop += list.scrollHeight - previousHeight;
      });
    } catch (loadError) {
      setError(getErrorMessage(loadError));
    } finally {
      setIsLoadingOlder(false);
    }
  };

  const handleDraftChange = (event) => {
    const value = event.target.value;
    setDraft(value);
    setSendError('');
    if (failedAttempt && failedAttempt.message !== value.trim()) setFailedAttempt(null);
  };

  const sendMessage = async (event) => {
    event?.preventDefault();
    const message = draft.trim();
    if (!message || isSending || conversation?.isClosed) return;

    const clientMessageId = failedAttempt?.message === message
      ? failedAttempt.clientMessageId
      : crypto.randomUUID();
    const payload = { message, clientMessageId };

    setIsSending(true);
    setSendError('');
    try {
      const raw = isCustomer
        ? await repositoryContainer.chat.sendCustomerMessage(
            subjectType,
            referenceCode,
            chatAccessToken,
            payload
          )
        : await repositoryContainer.chat.sendCompanyMessage(subjectType, subjectId, payload);
      const createdMessage = raw?.response ?? raw;
      setMessages((current) => mergeMessages(current, [createdMessage]));
      setDraft('');
      setFailedAttempt(null);
      window.requestAnimationFrame(() => {
        if (messageListRef.current) {
          messageListRef.current.scrollTop = messageListRef.current.scrollHeight;
        }
      });
    } catch (sendFailure) {
      setFailedAttempt({ message, clientMessageId });
      setSendError(sendFailure?.data?.message || 'Your message was not sent. Try again.');
    } finally {
      setIsSending(false);
    }
  };

  const renderedMessages = useMemo(() => messages.map((message) => {
    const senderType = String(message?.senderType || '').toLowerCase();
    const isOwn = isCustomer ? senderType === 'customer' : senderType !== 'customer';
    return { ...message, isOwn, normalizedSenderType: senderType };
  }), [isCustomer, messages]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[140] flex items-center justify-center bg-background/80 p-3 backdrop-blur-sm sm:p-4"
      onMouseDown={(event) => event.target === event.currentTarget && !isSending && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="conversation-title"
        className="flex h-[min(720px,92vh)] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-2xl"
      >
        <header className="flex flex-shrink-0 items-center justify-between border-b border-border px-4 py-3 sm:px-6">
          <div className="min-w-0">
            <h2 id="conversation-title" className="font-heading text-lg font-semibold text-foreground sm:text-xl">{title}</h2>
            <p className="mt-0.5 truncate text-xs text-muted-foreground font-caption">#{referenceCode}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isSending}
            className="rounded-lg p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
            aria-label="Close conversation"
          >
            <Icon name="X" size={20} />
          </button>
        </header>

        <div ref={messageListRef} className="flex-1 overflow-y-auto bg-muted/15 px-4 py-5 custom-scrollbar sm:px-6">
          {isLoading ? (
            <div className="space-y-4 animate-pulse" aria-label="Loading conversation">
              <div className="h-14 w-3/5 rounded-2xl bg-muted" />
              <div className="ml-auto h-16 w-2/3 rounded-2xl bg-muted" />
              <div className="h-12 w-1/2 rounded-2xl bg-muted" />
            </div>
          ) : error && messages.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-error/10 text-error">
                <Icon name="MessageSquareOff" size={23} />
              </span>
              <p className="mt-4 max-w-sm text-sm text-muted-foreground font-caption">{error}</p>
            </div>
          ) : (
            <div className="space-y-4">
              {hasOlderMessages && (
                <div className="text-center">
                  <Button type="button" variant="ghost" size="sm" loading={isLoadingOlder} onClick={loadOlderMessages}>
                    Load earlier messages
                  </Button>
                </div>
              )}
              {error && <p className="text-center text-xs text-error font-caption">{error}</p>}
              {renderedMessages.length === 0 && (
                <div className="py-16 text-center">
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon name="MessagesSquare" size={23} />
                  </span>
                  <p className="mt-4 text-sm font-medium text-foreground font-caption">Start the conversation</p>
                  <p className="mt-1 text-xs text-muted-foreground font-caption">Messages about this {subjectType === 'bookingRequest' ? 'request' : 'booking'} will appear here.</p>
                </div>
              )}
              {renderedMessages.map((message) => (
                <article key={message.id || message.clientMessageId} className={`flex ${message.isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[84%] rounded-2xl px-3.5 py-2.5 sm:max-w-[75%] ${
                    message.isOwn
                      ? 'rounded-br-md bg-primary text-primary-foreground'
                      : 'rounded-bl-md border border-border bg-card text-foreground'
                  }`}>
                    <p className={`text-[11px] font-medium font-caption ${message.isOwn ? 'text-primary-foreground/75' : 'text-muted-foreground'}`}>
                      {message.senderDisplayName || (message.isOwn ? 'You' : message.normalizedSenderType === 'customer' ? 'Customer' : 'Company')}
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed font-caption">{message.message}</p>
                    <p className={`mt-1 text-[10px] font-caption ${message.isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                      {formatMessageTime(message.createdAtUtc)}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        <footer className="flex-shrink-0 border-t border-border bg-card p-3 sm:px-5 sm:py-4">
          {conversation?.isClosed ? (
            <p className="rounded-lg bg-muted px-4 py-3 text-center text-sm text-muted-foreground font-caption">This conversation has been closed.</p>
          ) : (
            <form onSubmit={sendMessage}>
              <div className="flex items-end gap-2">
                <div className="min-w-0 flex-1">
                  <textarea
                    ref={textareaRef}
                    rows={2}
                    maxLength={MAX_MESSAGE_LENGTH}
                    value={draft}
                    onChange={handleDraftChange}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                    disabled={isSending || Boolean(error && messages.length === 0)}
                    placeholder="Write a message..."
                    className="max-h-32 min-h-[44px] w-full resize-none rounded-xl border border-input bg-background px-3 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 font-caption"
                  />
                </div>
                <Button
                  type="submit"
                  size="icon"
                  loading={isSending}
                  disabled={!draft.trim() || Boolean(error && messages.length === 0)}
                  aria-label="Send message"
                >
                  {!isSending && <Icon name="Send" size={17} />}
                </Button>
              </div>
              <div className="mt-1.5 flex items-start justify-between gap-3 px-1">
                <p className="text-xs text-error font-caption">{sendError}</p>
                <p className="ml-auto flex-shrink-0 text-[10px] text-muted-foreground font-caption">{draft.length}/{MAX_MESSAGE_LENGTH}</p>
              </div>
            </form>
          )}
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default ConversationModal;
